import {
  endOfISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { v4 as uuid } from 'uuid';

import { parseDate } from '~/eds/src/utils/parseDate';
import { FieldType } from '~/evifields/src/types/FieldType';
import { Filter } from '~/evifields/src/types/Filter';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';

const CROSS_FILTER_OPERATORS: Record<string, OperatorId> = {
  age: 'between',
  boolean: 'equals',
  text: 'text_contains_any',
  date: 'date_between',
  enum: 'equals',
  enum_set: 'contains_all',
  number: 'between',
};

const getValuesAndOperator: (
  type: FieldType,
  value: string,
) => { values: unknown[]; operatorId: OperatorId } = (type, value) => {
  switch (type) {
    case 'age':
      return {
        values: value.split(' - ').map((item) => Number(item)),
        operatorId: CROSS_FILTER_OPERATORS[type],
      };
    case 'date':
      const dateValues = value.split(' to ').map((date) => parseDate(date));
      if (dateValues.length > 1) {
        return {
          values: dateValues,
          operatorId: CROSS_FILTER_OPERATORS[type],
        };
      } else {
        return {
          values: dateValues,
          operatorId: 'date_on',
        };
      }

    case 'number':
      const numberValues = `${value}`
        .split(' - ')
        .map((numberValue) => Number(numberValue.replaceAll(',', '')));
      if (numberValues.length > 1) {
        return {
          values: numberValues,
          operatorId: CROSS_FILTER_OPERATORS[type],
        };
      } else {
        return {
          values: numberValues,
          operatorId: 'equals',
        };
      }

    case 'boolean':
      return {
        values: [],
        operatorId: value === 'True' ? 'is_true' : 'is_false',
      };
    case 'enum':
    case 'enum_set':
    default:
      return {
        values: [value],
        operatorId: CROSS_FILTER_OPERATORS[type],
      };
  }
};

export const toFilter = (
  fieldId: string,
  value: string,
  type: FieldType,
): Filter => {
  return {
    id: uuid(),
    fieldId,
    ...getValuesAndOperator(type, value),
  };
};

export const toBucketValue = (
  filterValues: Array<unknown>,
  type: FieldType,
  formatDate: useFormatDateReturnType,
) => {
  switch (type) {
    case 'date':
      return filterValues.length === 1
        ? formatDate(filterValues[0] as Date, 'iso')
        : filterValues
            .map((value) => formatDate(value as Date, 'iso'))
            .join(' to ');
    case 'boolean':
    case 'enum':
    case 'enum_set':
    case 'number':
    default:
      return filterValues.length === 1
        ? (filterValues[0] as string)
        : filterValues.join(' - ');
  }
};

export const getIntervalValues = (date: Date, interval: string) => {
  switch (interval) {
    case 'YEAR':
      return [startOfYear(date), endOfYear(date)];
    case 'QUARTER':
      return [startOfQuarter(date), endOfQuarter(date)];
    case 'MONTH':
      return [startOfMonth(date), endOfMonth(date)];
    case 'WEEK':
      return [startOfISOWeek(date), endOfISOWeek(date)];

    default:
      return [date];
  }
};
